import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tier,              setTier]              = useState('free');
  const [currentStreak,     setCurrentStreak]     = useState(0);
  const [longestStreak,     setLongestStreak]     = useState(0);
  const [totalPoints,       setTotalPoints]       = useState(0);
  const [pointsTier,        setPointsTier]        = useState('learner');
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const lastFetchedId = useRef(null); // prevent duplicate profile/points fetches

  // Fetch the full user_profiles row whenever user changes
  const fetchProfile = async (userId) => {
    if (!userId) {
      setProfile(null);
      setTier('free');
      setCurrentStreak(0);
      setLongestStreak(0);
      return;
    }
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, display_name, email, phone, background, learning_notes, age, role, tier, current_streak, longest_streak, last_lesson_date')
        .eq('id', userId)
        .single();
      setProfile(data || null);
      setTier(data?.tier || 'free');
      setCurrentStreak(data?.current_streak || 0);
      setLongestStreak(data?.longest_streak || 0);
    } catch {
      setProfile(null);
      setTier('free');
      setCurrentStreak(0);
      setLongestStreak(0);
    }
  };

  // Load points balance from user_points table
  const loadPoints = async (userId) => {
    if (!userId) { setTotalPoints(0); setPointsTier('learner'); return; }
    try {
      const { data } = await supabase
        .from('user_points')
        .select('total_points, tier')
        .eq('user_id', userId)
        .maybeSingle();
      setTotalPoints(data?.total_points || 0);
      setPointsTier(data?.tier || 'learner');
    } catch {
      setTotalPoints(0);
      setPointsTier('learner');
    }
  };

  useEffect(() => {
    // Restore existing Supabase session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);                          // unblock the UI immediately
      fetchProfile(session?.user?.id ?? null);    // load profile in background
      loadPoints(session?.user?.id ?? null);      // load points in background

      if (session?.user) {
        // Real authenticated user — clear any stale guest flag left from before sign-up
        setIsGuest(false);
        localStorage.removeItem('bl_guest_mode');
      } else if (localStorage.getItem('bl_guest_mode') === 'true') {
        // No real session — restore guest mode
        setIsGuest(true);
      }
    }).catch(() => {
      // Network error or Supabase timeout — unblock UI so splash doesn't get stuck
      setLoading(false);
    });

    // Keep in sync with Supabase auth state changes (sign-in, sign-out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setNeedsPasswordReset(true);
      }
      const newId = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      // Only re-fetch profile/points when the user ID actually changes (prevents
      // duplicate calls from INITIAL_SESSION + SIGNED_IN firing back-to-back)
      if (newId !== lastFetchedId.current) {
        lastFetchedId.current = newId;
        fetchProfile(newId);
        loadPoints(newId);
      }
      if (session?.user) {
        setIsGuest(false);
        localStorage.removeItem('bl_guest_mode');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth actions ────────────────────────────────────────────────────────────

  const signUp = async ({ email, password, displayName, phone }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: displayName, phone },
      },
    });
    if (!error && data.user) {
      await supabase.from('user_profiles').upsert({
        id:           data.user.id,
        display_name: displayName,
        phone,
      });
    }
    return { data, error };
  };

  const signIn = async ({ email, password }) => {
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signInWithGoogle = async () => {
    // Use the same redirect flow on native app and web.
    // The Capacitor WebView navigates to Google, then back to exam.bitelearning.ai
    // with ?code=XXX — the Supabase client auto-detects and exchanges the code.
    // (Chrome Custom Tab + custom URL scheme approach was unreliable on Android.)
    // Redirect back to the correct origin after Google auth.
    // exam.bitelearning.ai must be in Supabase Auth → URL Configuration → Redirect URLs.
    const host = window.location.hostname;
    const redirectTo = host === 'exam.bitelearning.ai'
      ? 'https://exam.bitelearning.ai'
      : host === 'bitelearning.ai'
        ? 'https://bitelearning.ai'
        : window.location.origin;
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsGuest(false);
    setProfile(null);
    localStorage.removeItem('bl_guest_mode');
  };

  const continueAsGuest = () => {
    localStorage.setItem('bl_guest_mode', 'true');
    setIsGuest(true);
  };

  const resetPassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) setNeedsPasswordReset(false);
    return { error };
  };

  // ── Update profile ──────────────────────────────────────────────────────────

  const updateProfile = async (updates) => {
    if (!user?.id) return { error: 'Not logged in' };
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (!error && data) setProfile(data);
    return { data, error };
  };

  // ── Derived values ──────────────────────────────────────────────────────────

  const displayName =
    profile?.display_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    null;

  const userRole     = profile?.role || 'user';
  const isAdmin      = userRole === 'admin' || userRole === 'super_admin';
  const isSuperAdmin = userRole === 'super_admin';
  const background     = profile?.background     || '';
  const learningNotes  = profile?.learning_notes || '';
  const userAge        = profile?.age            || null;
  const isAuthenticated = !!user || isGuest;

  return (
    <AuthContext.Provider value={{
      user, profile, isGuest, loading, isAuthenticated,
      displayName, background, learningNotes, userAge,
      userRole, isAdmin, isSuperAdmin,
      tier, currentStreak, longestStreak,
      setCurrentStreak, setLongestStreak,
      totalPoints, pointsTier, setTotalPoints, setPointsTier,
      needsPasswordReset, resetPassword,
      signUp, signIn, signInWithGoogle, signOut, continueAsGuest,
      updateProfile, fetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
